import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable } from 'mobx';
import UrlAssembler from 'url-assembler';
import { ACDLError } from '../../../../models/error.model';
import { IMainApiService } from '../../../../services/api.service';
import { IApiService } from '../../../../services/api.service.base';
import { ICanPublishProjectResponse } from '../../projectDetails.models';
import { IProjectDetailsSubmodule } from '../../services/IProjectDetailsSubmodule';
import { ToolStatisticsViewMode } from './projectDetailsStatistics.model';
import { IProjectDetailsStatisticsStore } from './projectDetailsStatistics.store';
import { IProjectDetailsStatisticsApiService } from './services/projectDetailsStatisticsApi.service';

export interface IProjectDetailsStatisticsBl extends IProjectDetailsSubmodule {
  statisticsSelectAllAnnotationType(): void;
  statisticsSelectAllQuestions(): void;
  statisticsSelectAllImageSets(): void;
  downloadStatisticsAsync(projectId: string): Promise<void | ACDLError>;
  getStatisticsAsync(projectId: string): Promise<void>;
  refreshToolStatistics(projectId: string): Promise<void>;
  getProjectToolStatisticsAsync(projectId: string): Promise<void>;
  statisticsSelectAnnotationType(annotationTypeId: string): void;
  statisticsSelectQuestion(questionId: string): void;
  statisticsSelectImageSet(imageSetId: string): void;
  resetToolStatistics(): void;
  getTextQuestionAnswers(questionId: string): Promise<void | ACDLError>;
  getTextAttributeAnswers(data: { attributeId: string; annotationTypeId: string }): Promise<void | ACDLError>;
  store: IProjectDetailsStatisticsStore;
}

export namespace IProjectDetailsStatisticsBl {
  export const $: interfaces.ServiceIdentifier<IProjectDetailsStatisticsBl> = Symbol('PROJECT_DETAILS_STATISTICS_SERVICE');
}

@injectable()
export class ProjectDetailsStatisticsBl implements IProjectDetailsStatisticsBl {
  constructor(
    @inject(IProjectDetailsStatisticsStore.$) public readonly store: IProjectDetailsStatisticsStore,
    @inject(IProjectDetailsStatisticsApiService.$) private readonly projectDetailsStatisticsApiService: IProjectDetailsStatisticsApiService,
    @inject(IMainApiService.$) private readonly apiService: IApiService
  ) {
    makeObservable(this);
  }

  validate(): void {
    // nop
  }

  initialize(): void {
    // nop
  }

  cleanup(): void {
    // nop
  }

  getValidationErrors(): string[] {
    return [];
  }

  handleCanPublishCheck(_: ICanPublishProjectResponse): void {
    // nop
  }

  @action
  statisticsSelectAllQuestions(): void {
    this.store.toolStatisticsViewMode = ToolStatisticsViewMode.ALL_QUESTIONS;
    this.store.selectedToolStatistics = undefined;
  }

  @action
  statisticsSelectAllAnnotationType(): void {
    this.store.toolStatisticsViewMode = ToolStatisticsViewMode.All_TOOLS;
    this.store.selectedToolStatistics = undefined;
  }

  @action
  statisticsSelectAllImageSets(): void {
    this.store.toolStatisticsViewMode = ToolStatisticsViewMode.ALL_IMAGE_SETS;
    this.store.selectedToolStatistics = undefined;
  }

  @action
  resetToolStatistics(): void {
    this.store.toolStatisticsViewMode = ToolStatisticsViewMode.NONE;
    this.store.selectedToolStatistics = undefined;
  }

  @action
  statisticsSelectAnnotationType(annotationTypeId: string): void {
    this.store.selectedToolStatistics = this.store.toolStatistics.annotationTypesStatistics.find((x) => x.annotationStatusStatistics.id === annotationTypeId);
    this.store.toolStatisticsViewMode = ToolStatisticsViewMode.TOOL;
  }

  @action
  statisticsSelectQuestion(questionId: string): void {
    this.store.selectedToolStatistics = this.store.toolStatistics.questionStatistics.find((x) => x.annotationStatusStatistics.id === questionId);
    this.store.toolStatisticsViewMode = ToolStatisticsViewMode.QUESTION;
  }

  @action
  statisticsSelectImageSet(imageSetId: string): void {
    this.store.selectedToolStatistics = this.store.toolStatistics.imageSetsStatistics.find((x) => x.annotationStatusStatistics.id === imageSetId);
    this.store.toolStatisticsViewMode = ToolStatisticsViewMode.IMAGE_SET;
  }

  @action
  async downloadStatisticsAsync(projectId: string): Promise<void | ACDLError> {
    const url = UrlAssembler()
      .template('/Projects/DownloadStatistics')
      .query({
        projectId,
      })
      .toString();

    const result = await this.apiService.getFileAsync(url);

    if (result instanceof ACDLError) {
      return result;
    }

    return undefined;
  }

  @action
  async getStatisticsAsync(projectId: string): Promise<void> {
    const result = await this.projectDetailsStatisticsApiService.getProjectStatisticsAsync(projectId, this.store.statisticsPaging);

    if (result instanceof ACDLError) {
      return;
    }

    if (result.pagesCount < result.pageNumber) {
      this.store.statisticsPaging.pageNumber = result.pagesCount;
      await this.getStatisticsAsync(projectId);
    } else {
      this.store.statistics.replace(result.data);
      this.store.statisticsPaging = {
        ...this.store.statisticsPaging,
        pageNumber: result.pageNumber,
        pagesCount: result.pagesCount,
        totalCount: result.totalCount,
      };
    }
  }

  @action
  async getProjectToolStatisticsAsync(projectId: string): Promise<void> {
    const result = await this.projectDetailsStatisticsApiService.getProjectToolStatistics(projectId);
    if (result instanceof ACDLError) {
      if (result.withCode(['NO_STATISTICS'])) {
        await this.refreshToolStatistics(projectId);
      }
      return;
    }
    this.store.toolStatistics = result;
  }

  @action
  async refreshToolStatistics(projectId: string): Promise<void> {
    const result = await this.projectDetailsStatisticsApiService.refreshToolStatistics(projectId);
    if (result instanceof ACDLError) {
      return;
    }
    this.store.toolStatistics = result;
  }

  @action.bound
  async getTextQuestionAnswers(questionId: string): Promise<void | ACDLError> {
    const result = await this.apiService.getFileAsync('/Projects/GetTextQuestionAnswers', { params: { questionId } });

    if (result instanceof ACDLError) {
      return result;
    }

    return undefined;
  }

  @action.bound
  async getTextAttributeAnswers(data: { attributeId: string; annotationTypeId: string }): Promise<void | ACDLError> {
    const result = await this.apiService.getFileAsync('/Projects/GetTextAttributeAnswers', { params: { ...data } });

    if (result instanceof ACDLError) {
      return result;
    }

    return undefined;
  }
}
